
import React, { useState, useEffect } from 'react';
import PlatformIcon from '../common/PlatformIcon';
import ReplyModal from './ReplyModal';
import { socialService, Comment } from '../../services/socialService';
import { formatTimeAgo } from '../../utils/dateFormatter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, MessageCircle, CheckCircle, Info, Globe, Facebook, Instagram, Music, Youtube } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useSocialConnections } from '../../hooks/useSocialConnections';
import { useAnalyticsRetention } from '../../hooks/useAnalyticsRetention';

// Helper to render platform icon
const getPlatformIcon = (iconName: string, className: string = "w-4 h-4") => {
  const icons: { [key: string]: React.ReactElement } = {
    'Globe': <Globe className={className} />,
    'Facebook': <Facebook className={className} />,
    'Instagram': <Instagram className={className} />,
    'Music': <Music className={className} />,
    'Youtube': <Youtube className={className} />
  };
  return icons[iconName] || <Globe className={className} />;
};

const RecentComments: React.FC = () => {
  const [platformFilter, setPlatformFilter] = useState<'all' | 'facebook' | 'instagram' | 'tiktok' | 'youtube'>('all');
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [repliedComments, setRepliedComments] = useState<Map<string, number>>(new Map());
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { connections, isLoading: connectionsLoading } = useSocialConnections();
  const { retentionDays, isUnlimited } = useAnalyticsRetention();

  // Load replied comments from localStorage on mount
  useEffect(() => {
    const savedReplies = localStorage.getItem('repliedComments');
    if (savedReplies) {
      try {
        const parsedReplies = JSON.parse(savedReplies);
        // Handle both old Set format and new Map format
        if (Array.isArray(parsedReplies)) {
          // Old format - convert to Map with count 1
          const replyMap = new Map();
          parsedReplies.forEach(id => replyMap.set(id, 1));
          setRepliedComments(replyMap);
        } else {
          // New format - already a Map
          setRepliedComments(new Map(Object.entries(parsedReplies)));
        }
      } catch (error) {
        console.error('Error loading replied comments from localStorage:', error);
      }
    }
  }, []);

  // Save replied comments to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('repliedComments', JSON.stringify(Object.fromEntries(repliedComments)));
  }, [repliedComments]);

  // Get available platforms based on connected accounts
  const allPlatforms = [
    { value: 'all', label: 'All Platforms', icon: 'Globe' },
    { value: 'facebook', label: 'Facebook', icon: 'Facebook' },
    { value: 'instagram', label: 'Instagram', icon: 'Instagram' },
    { value: 'tiktok', label: 'TikTok', icon: 'Music' },
    { value: 'youtube', label: 'YouTube', icon: 'Youtube' }
  ];

  // Filter platforms to only show connected ones (plus "All")
  const connectedPlatforms = React.useMemo(() => {
    if (!connections || connectionsLoading) {
      return [allPlatforms[0]]; // Only show "All" while loading
    }

    const connectedProviders = connections.map(conn => conn.provider.toLowerCase());
    console.log('🔗 Connected providers:', connectedProviders);
    
    const availablePlatforms = [
      allPlatforms[0], // Always include "All"
      ...allPlatforms.slice(1).filter(platform => 
        connectedProviders.includes(platform.value)
      )
    ];
    
    console.log('📱 Available platforms for comments:', availablePlatforms.map(p => p.value));
    return availablePlatforms;
  }, [connections, connectionsLoading]);

  // Reset platform filter if current selection is no longer available
  React.useEffect(() => {
    if (!connectedPlatforms.find(p => p.value === platformFilter)) {
      console.log('🔄 Resetting platform filter to "all" - current selection not available');
      setPlatformFilter('all');
    }
  }, [connectedPlatforms, platformFilter]);

  // Fetch comments using React Query
  const { data: comments = [], isLoading, error, refetch } = useQuery({
    queryKey: ['comments', platformFilter],
    queryFn: async () => {
      console.log('🔍 Fetching comments for platform:', platformFilter);
      console.log('🔗 Connected platforms:', connections?.map(c => c.provider));
      try {
        const result = await socialService.getComments(
          platformFilter === 'all' ? undefined : platformFilter, 
          10, 
          retentionDays || 30 // Use tier retention limit, default to 30 days
        );
        console.log('✅ Comments fetched successfully:', result);
        console.log('📊 Comments by platform:', result.reduce((acc, comment) => {
          acc[comment.platform] = (acc[comment.platform] || 0) + 1;
          return acc;
        }, {} as Record<string, number>));
        return result;
      } catch (err) {
        console.error('❌ Error fetching comments:', err);
        throw err;
      }
    },
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    retry: 1,
    // No auto-refetch - users can manually refresh if needed
  });

  // Reply mutation
  const replyMutation = useMutation({
    mutationFn: ({ commentId, message, platform }: { commentId: string; message: string; platform: string }) =>
      socialService.replyToComment(commentId, message, platform),
    onSuccess: (data, variables) => {
      // Increment reply count for this comment
      setRepliedComments(prev => {
        const newMap = new Map(prev);
        const currentCount = newMap.get(variables.commentId) || 0;
        newMap.set(variables.commentId, currentCount + 1);
        return newMap;
      });
      
      // Show success toast
      toast({
        title: 'Reply sent successfully!',
        description: `Your reply to ${selectedComment?.author || 'the comment'} has been posted.`,
        variant: 'default'
      });
      
      // Refetch comments after successful reply
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      setIsReplyModalOpen(false);
      setSelectedComment(null);
    },
    onError: (error: any) => {
      console.error('Failed to send reply:', error);
      
      // Show error toast
      toast({
        title: 'Failed to send reply',
        description: error?.response?.data?.detail || error?.message || 'Something went wrong. Please try again.',
        variant: 'destructive'
      });
    },
  });

  const handleReplyClick = (comment: Comment) => {
    setSelectedComment(comment);
    setIsReplyModalOpen(true);
  };

  const handleSendReply = (reply: string) => {
    if (selectedComment) {
      replyMutation.mutate({
        commentId: selectedComment.id,
        message: reply,
        platform: selectedComment.platform,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center space-x-2 text-slate-500 dark:text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading comments...</span>
        </div>
      </div>
    );
  }

  if (error) {
    console.error('❌ Comments error details:', error);
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="text-center">
          <MessageCircle className="w-12 h-12 text-slate-400 mx-auto mb-2" />
          <p className="text-slate-500 dark:text-slate-400">Failed to load comments</p>
          <p className="text-xs text-red-500 mt-1">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <button 
            onClick={() => refetch()}
            className="mt-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border-2 border-slate-200/60 dark:border-slate-600/40 rounded-2xl p-6 h-full flex flex-col shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-200">Recent Comments</h2>
            
            {/* Info Tooltip */}
            <div className="relative group">
              <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                <Info className="w-5 h-5" />
              </button>
              <div className="absolute left-0 top-8 w-80 p-4 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <p className="font-semibold mb-2 text-sm">Comment Display Info:</p>
                <ul className="space-y-1.5 text-xs text-slate-700 dark:text-slate-200">
                  <li>• Shows <strong>last 10 comments</strong> per platform</li>
                  <li>• Displays comments from last <strong>{isUnlimited ? 'unlimited' : `${retentionDays} days`}</strong> (based on your tier)</li>
                  <li>• Updates when you <strong>manually refresh</strong></li>
                  <li>• Click any comment to <strong>reply directly</strong></li>
                  <li>• Filter by platform using the dropdown</li>
                </ul>
                <div className="absolute -top-2 left-4 w-4 h-4 bg-white dark:bg-slate-800 border-l border-t border-slate-200 dark:border-slate-700 transform rotate-45"></div>
              </div>
            </div>
            
            <button
              onClick={() => {
                console.log('🔄 Manual refresh triggered');
                console.log('📊 Current platform filter:', platformFilter);
                refetch();
              }}
              className="p-1 text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 transition-colors"
              title="Refresh comments"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            {comments && comments.length > 0 && (
              <div className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                📊 {comments.length} Comments
              </div>
            )}

            {repliedComments.size > 0 && (
              <button
                onClick={() => {
                  // Get IDs of replied comments
                  const repliedIds = Array.from(repliedComments.keys());
                  
                  // Update the query data to remove replied comments
                  queryClient.setQueryData(['comments', platformFilter], (oldData: Comment[] | undefined) => {
                    if (!oldData) return [];
                    return oldData.filter(comment => !repliedIds.includes(comment.id));
                  });
                  
                  // Clear the replied status
                  setRepliedComments(new Map());
                  
                  toast({
                    title: 'Replied comments removed',
                    description: `${repliedIds.length} replied comment${repliedIds.length !== 1 ? 's' : ''} removed from the list.`,
                    variant: 'default'
                  });
                }}
                className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                title="Remove all replied comments from the list"
              >
                Clear Replied ({repliedComments.size})
              </button>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center space-x-2 px-3 py-2 bg-slate-100/50 dark:bg-slate-700/30 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-600/50 transition-all duration-200 text-sm font-medium backdrop-blur-sm"
              disabled={connectionsLoading}
            >
              {getPlatformIcon(connectedPlatforms.find(p => p.value === platformFilter)?.icon || 'Globe')}
              <span>{connectedPlatforms.find(p => p.value === platformFilter)?.label || 'All Platforms'}</span>
              <svg className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {isDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200/50 dark:border-slate-600/30 backdrop-blur-xl z-10">
                {connectedPlatforms.length === 1 ? (
                  <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400 text-center">
                    No social accounts connected
                  </div>
                ) : (
                  connectedPlatforms.map((platform) => (
                    <button
                      key={platform.value}
                      onClick={() => {
                        setPlatformFilter(platform.value as any);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-slate-100/50 dark:hover:bg-slate-700/50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        platformFilter === platform.value
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {getPlatformIcon(platform.icon)}
                      <span className="text-sm font-medium">{platform.label}</span>
                      {platform.value !== 'all' && (
                        <span className="ml-auto text-xs text-green-500">●</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-4 overflow-y-auto flex-grow pr-2">
          {comments.map((comment) => {
            const replyCount = repliedComments.get(comment.id) || 0;
            const hasReplied = replyCount > 0;
            const isCurrentlyReplying = replyMutation.isPending && selectedComment?.id === comment.id;
            
            return (
              <div key={comment.id} className={`flex items-start space-x-4 p-3 rounded-xl transition-all duration-200 hover:shadow-md ${
                hasReplied 
                  ? 'bg-green-50/50 dark:bg-green-900/10 border border-green-200/50 dark:border-green-800/30' 
                  : 'hover:bg-slate-100/50 dark:hover:bg-slate-700/40'
              }`}>
                <div className="flex-shrink-0">
                  <PlatformIcon
                    platform={comment.platform}
                    colorMode="brand"
                    className="w-6 h-6"
                  />
                </div>
                <div className="flex-grow">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      <span className="font-bold text-slate-900 dark:text-slate-200">{comment.author}</span> commented on{' '}
                      <span className="font-bold text-slate-900 dark:text-slate-200">{comment.post_title}</span>
                    </p>
                    {hasReplied && (
                      <div className="flex items-center space-x-1 px-2 py-1 bg-green-100 dark:bg-green-900/20 rounded-full">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        <span className="text-xs font-medium text-green-700 dark:text-green-400">
                          Replied {replyCount > 1 ? `(${replyCount}x)` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-slate-900 dark:text-slate-200 mt-1">{comment.text}</p>
                  
                  {/* Show existing replies */}
                  {comment.replies && comment.replies.length > 0 && (
                    <div className="mt-3 pl-4 border-l-2 border-slate-200 dark:border-slate-700 space-y-2">
                      {comment.replies.map((reply) => (
                        <div key={reply.id} className="text-sm">
                          <p className="text-slate-600 dark:text-slate-400">
                            <span className="font-semibold text-slate-800 dark:text-slate-300">{reply.author}</span>
                            {' '}
                            <span className="text-slate-900 dark:text-slate-200">{reply.text}</span>
                          </p>
                          <span className="text-xs text-slate-400 dark:text-slate-500">
                            {formatTimeAgo(reply.timestamp)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                      {formatTimeAgo(comment.timestamp)}
                    </span>
                    <button 
                      onClick={() => handleReplyClick(comment)}
                      disabled={replyMutation.isPending}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors shadow-sm ${
                        hasReplied
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : isCurrentlyReplying
                            ? 'bg-indigo-400 text-white cursor-not-allowed'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      {isCurrentlyReplying ? 'Replying...' : hasReplied ? 'Reply Again' : 'Reply'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {comments.length === 0 && (
            <div className="flex items-center justify-center h-full min-h-[200px]">
              <div className="text-center py-8 px-4">
                <MessageCircle className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400 mb-2 font-medium">No comments available</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 max-w-md mx-auto">
                  {!connections || connections.length === 0
                    ? 'Connect your social media accounts to see comments'
                    : platformFilter === 'all' 
                      ? 'No recent comments found across your connected accounts'
                      : platformFilter === 'youtube'
                        ? 'YouTube comments temporarily unavailable due to API limits. Please try again later.'
                        : `No ${platformFilter} comments found`
                  }
                </p>
                {platformFilter === 'youtube' && (
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 max-w-sm mx-auto">
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      💡 YouTube API has daily usage limits. Comments will be available once the quota resets.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {selectedComment && (
        <ReplyModal
          isOpen={isReplyModalOpen}
          onClose={() => {
            setIsReplyModalOpen(false);
            setSelectedComment(null);
          }}
          comment={selectedComment}
          onSend={handleSendReply}
          isLoading={replyMutation.isPending}
        />
      )}
    </>
  );
};

export default RecentComments;
